import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DetailsColumn,
  MiscColumn,
  NameContainer,
  SearchResultRow,
  SearchResultWrapper,
  Title,
  UniqueSymbol,
} from './styled';
import { Row, Grid, Badge, Box } from '@simplywallst/ui-core';
import If from '@/components/If';
import { CompanyIcon } from '@/components/CompanyIcon';
import { useTracking } from 'react-tracking';
import Toggle from './components/Toggle';
import { SecondaryListings } from './components/SecondaryListings';
import { useQuery } from '@tanstack/react-query';
import { queryKey, queryFn } from '../AlgoliaSearch/query';
import type { ISO2 } from '@/constants/countries';
import { CountryFlag } from '@/components/CountryFlag';

import type { SearchResult as Result } from '@/hooks/useSearch';

type Listing = Result['listings'][number];

interface SearchResultTrackingProps {
  event: MouseEvent | TouchEvent;
  listIndex: number;
  companyId: string;
  uniqueSymbol: string;
  canonicalUrl: string;
}

interface Props {
  active?: boolean;
  canonicalUrl: string;
  id: string;
  name: string;
  fullMatchUniqueSymbol?: string[];
  light?: boolean;
  listIndex: number;
  listings?: Listing[];
  market?: ISO2;
  scores?: number[];
  tag?: null | string;
  uniqueSymbol: string;
  tickerSymbol?: string;
  description: string;
  imageUrl: string;
  website?: string | null;
  type: string;
  handleBlur?: () => void;
  handleFocus?: () => void;
  handleSelect?: (props: SearchResultTrackingProps) => void;
  onClick?: () => void;
  primaryWrapper?: (
    child: ReactNode,
    props: Pick<
      Props,
      'id' | 'canonicalUrl' | 'uniqueSymbol' | 'name' | 'listIndex'
    >
  ) => ReactNode;
  secondaryWrapper?: (child: ReactNode, listing: Listing) => ReactNode;
}

export const SearchResult = ({
  active,
  canonicalUrl,
  fullMatchUniqueSymbol,
  handleBlur,
  handleFocus,
  id,
  light,
  listIndex,
  listings,
  market,
  name,
  primaryWrapper,
  secondaryWrapper,
  tag,
  type,
  uniqueSymbol,
  tickerSymbol,
  website,
}: Props) => {
  const { trackEvent } = useTracking();
  const [expand, setExpand] = useState(false);

  const { data, status } = useQuery({
    queryKey: [queryKey],
    queryFn,
    staleTime: Infinity,
  });

  useEffect(() => {
    setExpand(false);
  }, [id]);

  const [fullMatch, countryMatch, restListing] = useMemo(() => {
    /** Result hoisting logic here */
    const fullMatchListing: Listing[] = [];
    const countryMatchListing: Listing[] = [];
    const restListing: Listing[] = [];

    if (typeof listings === 'undefined') {
      return [fullMatchListing, countryMatchListing, restListing];
    }
    if (typeof fullMatchUniqueSymbol === 'undefined') {
      return [fullMatchListing, countryMatchListing, restListing];
    }

    listings.forEach((listing) => {
      /**
       * TS still thinks fullMatchUniqueSymbol can be undefined when
       * we already did a null checked above.
       */
      if ((fullMatchUniqueSymbol as string[]).includes(listing.uniqueSymbol)) {
        fullMatchListing.push(listing);
      } else {
        if (
          typeof data === 'string' &&
          data.toLowerCase() === listing.exchangeCountryIso.toLowerCase()
        ) {
          countryMatchListing.push(listing);
        } else {
          restListing.push(listing);
        }
      }
    });
    return [fullMatchListing, countryMatchListing, restListing];
  }, [
    fullMatchUniqueSymbol,
    listings,
    uniqueSymbol,
    canonicalUrl,
    market,
    data,
  ]);

  useEffect(() => {
    if (active && containerRef.current) {
      containerRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [active]);

  const toggleClick = useCallback(() => {
    trackEvent({
      action: !expand ? 'listing-expand' : 'listing-collapse',
    });
    setExpand(!expand);
  }, [expand, trackEvent]);

  const containerRef = useRef<HTMLDivElement>(null);

  if (status === 'pending') {
    return null;
  }

  if (type === 'company') {
    return (
      <SearchResultWrapper
        onBlur={handleBlur}
        onFocus={handleFocus}
        onClick={() => {
          trackEvent({
            action: 'select',
            index: listIndex,
            unique_symbol: uniqueSymbol,
            result_type: type,
          });
        }}
        data-cy-id={`${uniqueSymbol}-search-result`}
      >
        {typeof primaryWrapper === 'function' &&
          primaryWrapper(
            <SearchResultRow ref={containerRef} light={light} active={active}>
              <Box paddingLeft="x2">
                <CompanyIcon
                  website={website}
                  tickerSymbol={tickerSymbol ?? uniqueSymbol?.split?.(':')?.[1]}
                  size={40}
                />
              </Box>
              <NameContainer>
                <DetailsColumn>
                  <Row>
                    <Title data-cy-id="search-results-label" light={light}>
                      {name}
                    </Title>
                  </Row>
                  <Grid templateColumns="24px auto" alignItems="center">
                    <CountryFlag countryISO={market || null} />

                    <UniqueSymbol light={light}>{uniqueSymbol}</UniqueSymbol>
                  </Grid>
                </DetailsColumn>
                <MiscColumn>
                  {tag && <Badge>{tag}</Badge>}
                  <If
                    match={
                      restListing.length > 0 &&
                      fullMatch.length === 0 &&
                      countryMatch.length === 0
                    }
                  >
                    <Toggle
                      light={light}
                      expand={expand}
                      onClick={toggleClick}
                      listingCount={restListing.length}
                    />
                  </If>
                </MiscColumn>
              </NameContainer>
            </SearchResultRow>,
            {
              id,
              canonicalUrl,
              uniqueSymbol,
              name,
              listIndex,
            }
          )}
        <SecondaryListings
          light={light}
          fullMatch={fullMatch}
          countryMatch={countryMatch}
          listings={restListing}
          secondaryWrapper={secondaryWrapper}
          expand={expand}
          toggleElement={
            <Toggle
              light={light}
              expand={expand}
              onClick={toggleClick}
              listingCount={restListing.length}
            />
          }
        />
      </SearchResultWrapper>
    );
  } else {
    return <></>;
  }
};
